import { ColDef, ICellRendererParams, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { format } from "date-fns";
import { Plus, Trash2 } from "lucide-react";
import React from "react";

import {
  IgAlert,
  IgButton,
  IgDialog,
  IgIconButton,
  IgMonoText,
  IgTableWrapper,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import { WorkspaceSecret } from "../../../types";

/** Org-secrets management modal (560px). */
export interface SecretsManagementModalProps {
  open: boolean;
  onClose: () => void;
  secrets: WorkspaceSecret[];
  isLoading: boolean;
  onCreateSecret: (name: string, value: string) => void;
  onDeleteSecret: (id: string) => void;
  isMutating: boolean;
  /**
   * Who else in the org can read these secrets, in the domain's own words.
   * Org secrets are shared across every consumer of this shell, so the scope
   * sentence is a security statement to the person typing the value — it
   * cannot be a constant here, or one domain tells the other's users something
   * false about where their secret goes.
   */
  scopeMessage: string;
}
import { useImmer } from "use-immer";
import { useForm } from "@tanstack/react-form";

/**
 * Org-secret names: must start with a letter, then letters / digits /
 * underscores only (mirrors `CreateOrgSecretRequestBodySchema` in ig-types).
 */
const SECRET_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * Auto-uppercases keystrokes and strips characters that can never be part of a
 * valid secret name so the inline input only ever holds schema-valid text.
 */
const normalizeSecretName = (raw: string): string =>
  raw.toUpperCase().replace(/[^A-Z0-9_]/g, "");

/**
 * Per-grid context threaded into the cell renderers so the row action can trigger
 * deletion without recreating the renderer function on every render.
 */
interface SecretsGridContext {
  onDeleteSecret: (id: string) => void;
  deletingSecretId: string | null;
  isMutating: boolean;
  errorIconColor: string;
}

const NameCellRenderer = ({ data }: ICellRendererParams<WorkspaceSecret>) =>
  data ? (
    <IgMonoText value={data.name} sx={{ width: "100%", textAlign: "left" }} />
  ) : null;

const CreatedCellRenderer = ({ data }: ICellRendererParams<WorkspaceSecret>) =>
  data ? (
    <IgTypography
      variant="body2"
      color="text.secondary"
      sx={{ width: "100%", textAlign: "left" }}
    >
      {format(new Date(data.createdAtTs), "PP")}
    </IgTypography>
  ) : null;

const ActionsCellRenderer = ({
  data,
  context,
}: ICellRendererParams<WorkspaceSecret, unknown, SecretsGridContext>) => {
  if (!data) return null;
  const { onDeleteSecret, deletingSecretId, isMutating, errorIconColor } =
    context;
  const isBusy = isMutating || deletingSecretId === data.id;

  return (
    <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <IgIconButton
        size="small"
        iconSize={16}
        color="error"
        disabled={isBusy}
        onClick={() => onDeleteSecret(data.id)}
      >
        <Trash2 size={16} color={errorIconColor} />
      </IgIconButton>
    </Stack>
  );
};

const baseColDef: ColDef<WorkspaceSecret> = {
  resizable: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  cellStyle: { display: "flex", alignItems: "center" },
};

const columnDefs: ColDef<WorkspaceSecret>[] = [
  {
    ...baseColDef,
    headerName: "Name",
    field: "name",
    flex: 1,
    minWidth: 160,
    cellRenderer: NameCellRenderer,
  },
  {
    ...baseColDef,
    headerName: "Created",
    field: "createdAtTs",
    width: 130,
    cellRenderer: CreatedCellRenderer,
  },
  {
    ...baseColDef,
    headerName: "",
    colId: "actions",
    width: 130,
    cellRenderer: ActionsCellRenderer,
  },
];

const NoSecretsOverlay = () => (
  <div style={{ padding: "20px", textAlign: "center" }}>
    <IgTypography color="text.secondary">No org secrets yet.</IgTypography>
  </div>
);

/**
 * Org-secrets management modal (560px). Lists org secrets (name in mono +
 * created date) in an AgGrid table with an inline delete action on each row,
 * an inline "+ Add org secret" form that auto-uppercases the name, and an info
 * callout explaining values are never shown, followed by the domain's own
 * `scopeMessage` naming who else can read them. Consumes
 * {@link SecretsManagementModalProps}.
 */
export const SecretsManagementModal = ({
  open,
  onClose,
  secrets,
  isLoading,
  scopeMessage,
  onCreateSecret,
  onDeleteSecret,
  isMutating,
}: SecretsManagementModalProps) => {
  const theme = useTheme();
  const [deletingSecretId, setDeletingSecretId] = useImmer<string | null>(null);

  const addSecretForm = useForm({
    defaultValues: { name: "", value: "" },
    onSubmit: ({ value }) => {
      if (!SECRET_NAME_PATTERN.test(value.name) || value.value.length === 0) {
        return;
      }
      onCreateSecret(value.name, value.value);
      resetAddForm();
    },
  });

  const [isAdding, setIsAdding] = useImmer(false);

  React.useEffect(() => {
    if (!isMutating && deletingSecretId !== null) {
      setDeletingSecretId(null);
    }
  }, [isMutating, deletingSecretId, setDeletingSecretId]);

  const handleDeleteSecret = (id: string) => {
    setDeletingSecretId(id);
    onDeleteSecret(id);
  };

  const resetAddForm = () => {
    setIsAdding(false);
    addSecretForm.reset();
  };

  const gridTheme = themeAlpine.withParams({
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
  });

  const gridContext: SecretsGridContext = {
    onDeleteSecret: handleDeleteSecret,
    deletingSecretId,
    isMutating,
    errorIconColor: theme.igIconColors.error,
  };

  const content = (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <IgAlert
        alertKey="org-secrets-info"
        severity="info"
        message={`Secret values are never shown after they are saved. ${scopeMessage}`}
      />

      <IgTableWrapper height={null} minHeight={"80px"}>
        <AgGridReact<WorkspaceSecret>
          theme={gridTheme}
          rowData={secrets}
          columnDefs={columnDefs}
          context={gridContext}
          getRowId={(params) => params.data.id}
          headerHeight={35}
          rowHeight={44}
          domLayout="autoHeight"
          suppressCellFocus
          enableCellTextSelection
          suppressMovableColumns
          suppressContextMenu
          animateRows={false}
          loading={isLoading}
          noRowsOverlayComponent={NoSecretsOverlay}
        />
      </IgTableWrapper>

      {isAdding ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-end"
          sx={{ width: "100%" }}
        >
          <addSecretForm.Field
            name="name"
            validators={{
              onChange: ({ value }) =>
                value.length === 0 || SECRET_NAME_PATTERN.test(value)
                  ? undefined
                  : "Must start with a letter; letters, digits, and underscores only.",
            }}
          >
            {(field) => (
              <IgTextField
                label="Name"
                placeholder="SECRET_NAME"
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(normalizeSecretName(e.target.value))
                }
                error={field.state.meta.errors.length > 0}
                slotProps={{
                  input: { style: { fontFamily: "monospace" } },
                }}
                sx={{ flex: 1 }}
              />
            )}
          </addSecretForm.Field>

          <addSecretForm.Field name="value">
            {(field) => (
              <IgTextField
                label="Value"
                type="password"
                placeholder="Value (write-only)"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                sx={{ flex: 1 }}
              />
            )}
          </addSecretForm.Field>
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <addSecretForm.Subscribe selector={(s) => s.values}>
              {(values) => (
                <IgButton
                  size="small"
                  variant="contained"
                  loading={isMutating}
                  disabled={
                    !SECRET_NAME_PATTERN.test(values.name) ||
                    values.value.length === 0 ||
                    isMutating
                  }
                  onClick={() => void addSecretForm.handleSubmit()}
                >
                  Save
                </IgButton>
              )}
            </addSecretForm.Subscribe>
            <IgButton
              size="small"
              variant="text"
              color="secondary"
              disabled={isMutating}
              onClick={resetAddForm}
            >
              Cancel
            </IgButton>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row">
          <IgButton
            variant="text"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setIsAdding(true)}
          >
            Add org secret
          </IgButton>
        </Stack>
      )}
    </Stack>
  );

  return (
    <IgDialog
      showDialog={open}
      title="Manage org secrets"
      handleClose={onClose}
      handleNoClick={onClose}
      hideActions
      minWidth="560px"
      maxWidth="sm"
      dialogContentWidth="100%"
      loading={isLoading}
      content={content}
    />
  );
};
